import React from 'react';
import { View, Text, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../theme/useTheme';
import { styles } from './KfaDataTable.styles';

export interface KfaTablePaginationProps {
  activePage: number;
  totalPages: number;
  currentPage: number;
  rowsPerPage: number;
  leftSecondaryText?: string;
  dropdownVisible: boolean;
  isServerSide: boolean;
  setDropdownVisible: (v: boolean) => void;
  setCurrentPage: (p: number) => void;
  onExternalPageChange?: (p: number) => void;
  onRowsPerPageChange: (size: number) => void;
}

const rowsPerPageOptions = [10, 25, 50, 100];

const MobilePagination: React.FC<Pick<
  KfaTablePaginationProps,
  'activePage' | 'totalPages' | 'currentPage' | 'isServerSide' | 'setCurrentPage' | 'onExternalPageChange'
>> = ({ activePage, totalPages, currentPage, isServerSide, setCurrentPage, onExternalPageChange }) => (
  <View style={styles.mobilePaginationContainer}>
    <View style={styles.mobilePaginationBox}>
      <TouchableOpacity
        disabled={activePage === 1}
        onPress={() => isServerSide ? onExternalPageChange?.(activePage - 1) : setCurrentPage(currentPage - 1)}
        style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell, activePage === 1 && styles.mobilePaginationCellDisabled]}
        testID="mobile-prev-page-button"
        accessibilityLabel="Previous page"
      >
        <MaterialIcons name="chevron-left" size={16} color={activePage === 1 ? "#A0A0A0" : "#005CBC"} />
      </TouchableOpacity>

      <View style={[styles.mobilePaginationCell, styles.mobilePaginationCurrentCell]}>
        <Text style={styles.mobilePaginationCurrentText}>{activePage}</Text>
      </View>

      <View style={[styles.mobilePaginationCell, styles.mobilePaginationNextCell]}>
        <Text style={styles.mobilePaginationNextText}>
          {activePage < totalPages ? activePage + 1 : activePage}
        </Text>
      </View>

      <TouchableOpacity
        disabled={activePage === totalPages}
        onPress={() => isServerSide ? onExternalPageChange?.(activePage + 1) : setCurrentPage(currentPage + 1)}
        style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell, activePage === totalPages && styles.mobilePaginationCellDisabled]}
        testID="mobile-next-page-button"
        accessibilityLabel="Next page"
      >
        <MaterialIcons name="chevron-right" size={16} color={activePage === totalPages ? "#A0A0A0" : "#005CBC"} />
      </TouchableOpacity>
    </View>
  </View>
);

const DesktopPagination: React.FC<KfaTablePaginationProps> = ({
  activePage, totalPages, currentPage, rowsPerPage, leftSecondaryText, dropdownVisible,
  isServerSide, setDropdownVisible, setCurrentPage, onExternalPageChange, onRowsPerPageChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <View style={[styles.paginationContainer, isDark && { borderTopColor: colors.border.default }]}>
      <View style={styles.rowsPerPageContainer}>
        <Text style={[styles.rowsPerPageLabel, isDark && { color: colors.text.secondary }]}>
          Rows per page
        </Text>
        <View style={{ position: "relative" }}>
          <TouchableOpacity
            style={[
              styles.dropdownContainer,
              isDark && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
            onPress={() => setDropdownVisible(!dropdownVisible)}
            testID="rows-per-page-dropdown"
            accessibilityLabel="Rows per page selector"
          >
            <Text style={[styles.dropdownValue, isDark && { color: colors.text.primary }]}>
              {rowsPerPage}
            </Text>
            <MaterialIcons
              name={dropdownVisible ? "keyboard-arrow-up" : "keyboard-arrow-down"}
              size={20}
              color={isDark ? colors.text.secondary : "#616161"}
            />
          </TouchableOpacity>
          {dropdownVisible && (
            <View
              style={[
                styles.dropdownOptionsContainer,
                isDark && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.border.default,
                },
              ]}
            >
              {rowsPerPageOptions.map((option) => (
                <TouchableOpacity
                  key={option}
                  style={[
                    styles.dropdownOption,
                    isDark && { borderBottomColor: colors.border.default },
                    option === rowsPerPage &&
                      (isDark ? { backgroundColor: 'rgba(61,139,255,0.22)' } : styles.dropdownOptionSelected),
                  ]}
                  onPress={() => { onRowsPerPageChange(option); setDropdownVisible(false); }}
                  testID={`rows-per-page-option-${option}`}
                  accessibilityLabel={`${option} rows per page`}
                >
                  <Text
                    style={[
                      styles.dropdownOptionText,
                      isDark && { color: colors.text.primary },
                      option === rowsPerPage && styles.dropdownOptionTextSelected,
                    ]}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
        {leftSecondaryText ? (
          <Text style={[styles.rowsPerPageSecondaryText, isDark && { color: colors.text.secondary }]}>
            {leftSecondaryText}
          </Text>
        ) : null}
      </View>

      <View style={styles.pageNavigationContainer}>
        <TouchableOpacity
          disabled={activePage === 1}
          onPress={() => isServerSide ? onExternalPageChange?.(1) : setCurrentPage(1)}
          style={styles.navigationButtonNoBorder}
          testID="first-page-button"
          accessibilityLabel="First page"
        >
          <MaterialIcons
            name="first-page"
            size={20}
            color={activePage === 1 ? (isDark ? "#555555" : "#D0D0D0") : (isDark ? colors.brand.primary : "#034895")}
          />
        </TouchableOpacity>

        <TouchableOpacity
          disabled={activePage === 1}
          onPress={() => isServerSide ? onExternalPageChange?.(activePage - 1) : setCurrentPage(currentPage - 1)}
          style={styles.navigationButtonNoBorder}
          testID="prev-page-button"
          accessibilityLabel="Previous page"
        >
          <MaterialIcons
            name="keyboard-arrow-left"
            size={20}
            color={activePage === 1 ? (isDark ? "#555555" : "#D0D0D0") : (isDark ? colors.brand.primary : "#034895")}
          />
        </TouchableOpacity>

        <View style={styles.pageIndicatorContainer}>
          <Text style={[styles.pageText, isDark && { color: colors.text.secondary }]}>Page</Text>
          <View
            style={[
              styles.currentPageBox,
              isDark && {
                backgroundColor: colors.background.surface,
                borderColor: colors.brand.primary,
              },
            ]}
          >
            <Text style={[styles.currentPageText, isDark && { color: colors.text.primary }]}>
              {activePage}
            </Text>
          </View>
          <Text style={[styles.pageOfText, isDark && { color: colors.text.secondary }]}>
            of <Text style={[styles.totalPagesText, isDark && { color: colors.text.primary }]}>{totalPages}</Text>
          </Text>
        </View>

        <TouchableOpacity
          disabled={activePage === totalPages}
          onPress={() => isServerSide ? onExternalPageChange?.(activePage + 1) : setCurrentPage(currentPage + 1)}
          style={styles.navigationButtonNoBorder}
          testID="next-page-button"
          accessibilityLabel="Next page"
        >
          <MaterialIcons
            name="keyboard-arrow-right"
            size={20}
            color={activePage === totalPages ? (isDark ? "#555555" : "#D0D0D0") : (isDark ? colors.brand.primary : "#034895")}
          />
        </TouchableOpacity>

        <TouchableOpacity
          disabled={activePage === totalPages}
          onPress={() => isServerSide ? onExternalPageChange?.(totalPages) : setCurrentPage(totalPages)}
          style={styles.navigationButtonNoBorder}
          testID="last-page-button"
          accessibilityLabel="Last page"
        >
          <MaterialIcons
            name="last-page"
            size={20}
            color={activePage === totalPages ? (isDark ? "#555555" : "#D0D0D0") : (isDark ? colors.brand.primary : "#034895")}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const KfaTablePagination: React.FC<KfaTablePaginationProps> = (props) => {
  const { width: screenWidth } = useWindowDimensions();
  const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;
  if (isIOSMobile) {
    return <MobilePagination {...props} />;
  }
  return <DesktopPagination {...props} />;
};

export default KfaTablePagination;
